import { useEffect, useRef } from 'react';
import { useSeoMeta } from '@unhead/react';

/**
 * Renders the standalone VicRoads digital licence prototype (public/digital-licence.html)
 * full-screen inside the app shell.
 *
 * The prototype runs in a same-origin iframe, but iOS Safari only streams
 * `deviceorientation` data to the *top-level* document and only lets a real
 * top-level gesture ask for sensor permission. So the top-level page owns the
 * tilt sensor: it requests access, listens for `deviceorientation`, and streams
 * the normalised tilt into the iframe via postMessage. The prototype asks for it
 * by posting a `holo-request` message when its "Enable hologram" button is tapped.
 */
const Index = () => {
  useSeoMeta({
    title: 'VicRoads — Digital Licence',
    description: 'A digital driver licence concept app.',
  });

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const post = (message: Record<string, unknown>) => {
      const win = iframe.contentWindow;
      if (win) win.postMessage(message, window.location.origin);
    };

    let streaming = false;
    let rafId = 0;
    let lastSent = 0;
    let lastX = 0;
    let lastY = 0;
    let pending: { nx: number; ny: number } | null = null;

    const flush = (now: number) => {
      rafId = 0;
      if (!pending) return;
      // The sensor fires far faster than we can use; throttle to ~30fps.
      if (now - lastSent < 33) {
        rafId = requestAnimationFrame(flush);
        return;
      }
      lastSent = now;
      if (Math.abs(pending.nx - lastX) < 0.01 && Math.abs(pending.ny - lastY) < 0.01) {
        pending = null;
        return;
      }
      lastX = pending.nx;
      lastY = pending.ny;
      post({ type: 'holo', nx: pending.nx, ny: pending.ny });
      pending = null;
    };

    const onOrientation = (event: DeviceOrientationEvent) => {
      const gamma = event.gamma == null ? 0 : event.gamma;
      const beta = event.beta == null ? 0 : event.beta;
      pending = {
        nx: Math.max(-1, Math.min(1, gamma / 45)),
        ny: Math.max(-1, Math.min(1, (beta - 35) / 45)),
      };
      if (!rafId) rafId = requestAnimationFrame(flush);
    };

    const startStreaming = () => {
      if (streaming) return;
      streaming = true;
      window.addEventListener('deviceorientation', onOrientation, true);
      post({ type: 'holo-granted' });
    };

    const Orientation = window.DeviceOrientationEvent as
      | (typeof DeviceOrientationEvent & { requestPermission?: () => Promise<PermissionState> })
      | undefined;
    const needsPermission = typeof Orientation?.requestPermission === 'function';

    const requestAccess = () => {
      if (!needsPermission) {
        startStreaming();
        return;
      }
      if (streaming) return;
      try {
        Orientation!.requestPermission!()
          .then((state) => {
            if (state === 'granted') startStreaming();
            else post({ type: 'holo-denied' });
          })
          // No transient activation in this frame: let the prototype, which
          // still holds the tap gesture, open the prompt instead.
          .catch(() => post({ type: 'holo-error' }));
      } catch {
        post({ type: 'holo-error' });
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow) return;
      const data = event.data as { type?: string } | null;
      if (data?.type === 'holo-request') requestAccess();
      // The prototype granted permission itself; just start streaming to it.
      else if (data?.type === 'holo-on') startStreaming();
    };

    window.addEventListener('message', onMessage);

    // Android and desktop expose orientation without a permission prompt, so
    // start streaming right away. iOS waits for the in-prototype tap.
    if (!needsPermission) startStreaming();

    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('deviceorientation', onOrientation, true);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div className="relative h-screen w-full overflow-hidden bg-[#eef0f3]">
      <iframe
        ref={iframeRef}
        src="/digital-licence.html?v=123"
        title="VicRoads digital driver licence"
        allow="accelerometer; gyroscope; magnetometer"
        className="h-full w-full border-0"
      />
      <a
        href="https://shakespeare.diy"
        target="_blank"
        rel="noreferrer noopener"
        className="absolute bottom-2 left-2 z-10 hidden rounded-full bg-white/70 px-3 py-1 text-[11px] font-medium text-[#1a2b4c] shadow-sm backdrop-blur transition-colors hover:bg-white sm:block"
      >
        Vibed with Shakespeare
      </a>
    </div>
  );
};

export default Index;
